<template>
  <li class="sidebar-menu-item" v-if="visible !== false">
    <div
      class="mi"
      :class="{ 'is-expanded': expanded, 'has-children': hasChildren }"
      role="button"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="onHeaderClick"
    >
      <!-- icon -->
      <i :class="item.icon" v-if="iconType === 'fontawesome'" />
      <img v-else :src="icon" class="menu_group_icon" alt="" />

      <!-- title: if has children, act as toggle; otherwise it's a link -->
      <template v-if="hasChildren">
        <span class="item-title fs14-700">{{ item.title }}</span>
      </template>
      <template v-else>
        <router-link :to="item.href" class="item-title fs14-700" @click.stop>{{ item.title }}</router-link>
      </template>

      <!-- arrow (only if children) -->
      <ArrowDown v-if="hasChildren" :rotated="expanded" class="arrow" @click.stop="toggle" />
    </div>

    <!-- subitems -->
    <transition name="collapse-vert">
      <div class="subitems" v-show="expanded" :id="'subitems_' + id">
        <sidebar-menu-subitem v-for="subitem in item.child" :data="subitem" :key="subitem.href || subitem.title" />
      </div>
    </transition>

    <div class="separator"></div>
  </li>
</template>

<script>
import SidebarMenuSubitem from "~/components/Sidebar/SidebarMenuSubitem.vue";
import ArrowDown from "@/assets/ComponentIcons/ArrowDown.vue";
import { getBaseRouteForMenuSelection } from '~/helpers/common';

export default {
  name: "SidebarMenuItem",
  props: {
    data: { type: Object, required: true },
    iconType: { type: String, default: "image" }, // 'fontawesome' | 'image'
    visible: { type: Boolean, default: true },
    // optional: start expanded if true
    defaultExpanded: { type: Boolean, default: false },
  },
  components: { SidebarMenuSubitem, ArrowDown },
  data() {
    return {
      item: this.data ?? {},
      expanded: false,
    };
  },
  computed: {
    id() {
      // stable id by href or title fallback
      return (this.item.href || this.item.title || Math.random().toString()).replace(/[^a-zA-Z0-9_-]/g, "");
    },
    icon() {
      try {
        return require("@/assets/" + this.item.icon);
      } catch (e) {
        return null;
      }
    },
    hasChildren() {
      return Array.isArray(this.item.child) && this.item.child.length > 0;
    },
    isActiveSelf() {
      return this.item?.href && getBaseRouteForMenuSelection(this.$route?.path || "/") === this.item.href;
    },
    isActiveDescendant() {
      if (!this.hasChildren) return false;
      return this.item.child.some((c) => c.href && getBaseRouteForMenuSelection(this.$route?.path || "/") === c.href);
    },
  },
  methods: {
    toggle() {
      if (!this.hasChildren) return;
      this.expanded = !this.expanded;
      this.$emit("onCollapse", { item: this.item, expanded: this.expanded });
    },
    onHeaderClick() {
      // if has children, toggle; else let router-link handle navigation
      if (this.hasChildren) {
        this.toggle();
      }
    },
    initExpanded() {
      // start expanded if defaultExpanded or route is inside this group
      this.expanded = !!(this.defaultExpanded || this.isActiveSelf || this.isActiveDescendant);
    },
  },
  created() {
    this.initExpanded();
  },
  watch: {
    data: {
      handler(newVal) {
        this.item = newVal || {};
        this.$nextTick(this.initExpanded);
      },
      deep: true,
    },
    $route() {
      // react to route change to auto-open the active branch
      if (this.isActiveDescendant || this.isActiveSelf) {
        if (!this.expanded) this.expanded = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global/global.scss";

.sidebar-menu-item {
  width: 100%;
  padding-top: 8px;
  background-color: $gray-10;

  .mi {
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    user-select: none;

    &.has-children .item-title {
      pointer-events: none;
    } /* text toggles */

    .menu_group_icon {
      display: flex;
      width: 20px;
      height: 20px;
      object-fit: contain;
      color: $brand;
      font-weight: 700;
    }

    &.is-expanded .menu_group_icon {
      color: $gray-80;
    }

    .item-title {
      flex: 1;
      padding-left: 8px;
      color: $brand;
      font-size: 14px;
      font-weight: 700;
      text-decoration: none;
    }

    .arrow {
      width: 12px;
      height: 10px;
      margin-left: 8px;
      margin-right: 8px;
      color: $brand;
    }

    &.is-expanded .arrow {
      color: $gray-80;
    }
  }

  .subitems {
    padding-top: 6px;
  }

  .separator {
    padding-top: 16px;
    margin-bottom: 10px;
    border-bottom: 1.8px solid $gray-30;
  }
}

/* simple vertical collapse transition */
.collapse-vert-enter-active,
.collapse-vert-leave-active {
  transition: max-height 0.18s ease, opacity 0.18s ease;
}
.collapse-vert-enter,
.collapse-vert-leave-to {
  max-height: 0;
  opacity: 0;
}
.collapse-vert-enter-to,
.collapse-vert-leave {
  max-height: 600px; /* enough for submenu items */
  opacity: 1;
}
</style>
